import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hospital } from "lucide-react";

export default function ShippingPolicy() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-lg bg-blue-600 p-2">
              <Hospital className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">AMCARE</h1>
          </Link>
          <Button variant="outline" asChild>
            <Link to="/login">Login</Link>
          </Button>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Shipping Policy</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Digital Service Delivery</h2>
              <p className="text-gray-700 mb-3">
                AMCare is a Software-as-a-Service (SaaS) platform. We provide digital healthcare 
                management services that are delivered electronically through our cloud-based platform. 
                <strong className="font-semibold"> No physical products are shipped.</strong>
              </p>
              <p className="text-gray-700">
                Upon successful subscription payment, you will receive immediate access to our 
                platform through your web browser or mobile application.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Service Activation</h2>
              <div className="space-y-2 text-gray-700">
                <p>
                  <strong>Immediate Access:</strong> Your subscription is activated immediately 
                  upon successful payment confirmation.
                </p>
                <p>
                  <strong>Account Setup:</strong> You can access your account and begin using 
                  the service right away after registration and payment.
                </p>
                <p>
                  <strong>No Shipping Required:</strong> Since this is a digital service, there 
                  is no shipping address required and no physical delivery involved.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Service Availability</h2>
              <p className="text-gray-700 mb-2">
                Our service is available 24/7 through:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Web browser (Chrome, Firefox, Safari, Edge)</li>
                <li>Any device with internet connectivity</li>
                <li>No installation or physical setup required</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Delivery Method</h2>
              <p className="text-gray-700">
                Service delivery is instant and digital. You will receive:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mt-2">
                <li>Email confirmation with account access details</li>
                <li>Immediate platform access via login credentials</li>
                <li>Access to all subscribed features and services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Geographic Availability</h2>
              <p className="text-gray-700">
                AMCare services are available to healthcare facilities in India. The service 
                can be accessed from anywhere with internet connectivity, subject to local 
                regulations and internet availability.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Technical Requirements</h2>
              <p className="text-gray-700 mb-2">
                To access our service, you need:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Stable internet connection</li>
                <li>Modern web browser (latest version recommended)</li>
                <li>Valid email address for account creation</li>
                <li>Device with screen resolution of at least 1024x768</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Service Updates</h2>
              <p className="text-gray-700">
                Platform updates and new features are delivered automatically as part of the 
                service. No manual updates or installations are required from your end.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Support and Assistance</h2>
              <p className="text-gray-700 mb-2">
                If you need help accessing or setting up your account:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Check your email for account activation instructions</li>
                <li>Contact our support team at <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a></li>
                <li>Visit our <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us</a> page</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Contact Us</h2>
              <p className="text-gray-700 mb-2">
                For questions regarding service delivery or access, please contact us:
              </p>
              <div className="text-gray-700">
                <p>Email: <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a></p>
                <p className="mt-2">Visit: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us Page</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} AMCare. All rights reserved.</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:underline">Terms & Conditions</Link>
            <Link to="/contact-us" className="hover:underline">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

